import { ReactElement, useCallback, useEffect, useState } from "react";

import './RadioDropdown.css';
import RadioDropdownItem from "./RadioDropdownItem/RadioDropdownItem";
import DropdownItem from "../../../models/DropdownItem.model";

const RadioDropdown = (props: {
  id: string;
  icon: ReactElement;
  label: string;
  selectedValue: any;
  items: DropdownItem[];
  onItemChanged: Function;
}) => {
  const [dropdownElementsList, setDropdownElementsList] = useState<
    ReactElement[]
  >([]);
  const [dropdownFilterText, setDropdownFilterText] = useState<string>("");
  const [selectedDropdownLabel, setSelectedDropdownLabel] = useState<any>(props.selectedValue);

  // Variables
  const { onItemChanged } = { ...props };

  const dropdownValueSelectedHandler = useCallback(
    (dropdownLabel: string, dropdownValue: any) => {
      setSelectedDropdownLabel(dropdownLabel);
      onItemChanged(dropdownValue);
    },
    [onItemChanged]
  );

  const getDropdownItemElements = useCallback(
    (items: DropdownItem[]): ReactElement[] => {
      const dropdownItemsList: ReactElement[] = items.map((item, idx) => (
        <RadioDropdownItem
          key={`${item.name}-${idx}`}
          id={`${item.name}-${idx}`}
          name={`${props.id}-radio-dropdown-${idx}`}
          label={item.name}
          value={item.value.toString()}
          defaultValue={props.selectedValue.toString()}
          onValueSelected={dropdownValueSelectedHandler}
        />
      ));

      return dropdownItemsList;
    },
    [props.id, props.selectedValue, dropdownValueSelectedHandler]
  );

  const dropdownFilterTextChangeHandler = useCallback(
    (filterText: string) => {
      const filteredDropdownItemsList: DropdownItem[] = props.items.filter(
        (item) =>
          item.name.toLowerCase().includes(filterText.trim().toLowerCase())
      );
      const dropdownElementsList = getDropdownItemElements(
        filteredDropdownItemsList
      );
      setDropdownElementsList(dropdownElementsList);
    },
    [getDropdownItemElements, props.items]
  );

  // Getting initial dropdown item JSX elements
  useEffect(() => {
    const allDropdownItems: ReactElement[] = getDropdownItemElements(
      props.items
    );
    setDropdownElementsList(allDropdownItems);

    const defaultValueIdx: number = props.items.findIndex(
      (item) => item.value.toString() === props.selectedValue.toString()
    );
    setSelectedDropdownLabel((defaultValueIdx > -1) ? props.items[defaultValueIdx].name : "");
  }, [getDropdownItemElements, props.id, props.items, props.selectedValue]);

  // Filter dropdown items after 500 ms of entering filter text
  useEffect(() => {
    const textFilterTimeoutId = setTimeout(() => {
      dropdownFilterTextChangeHandler(dropdownFilterText);
    }, 500);

    return () => clearTimeout(textFilterTimeoutId);
  }, [dropdownFilterText, dropdownFilterTextChangeHandler]);

  return (
    <div className="dropdown d-inline me-1">
      <button
        type="button"
        className="btn btn-sm btn-outline-dark dropdown-toggle"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        <span>
          {props.icon && props.icon} &nbsp;{props.label}
        </span>
        {selectedDropdownLabel && `: ${selectedDropdownLabel}`}
      </button>

      <ul className="dropdown-menu scrollable-dropdown">
        <li>
          <input
            type="text"
            id={`${props.id}-text-filter`}
            className="form-control form-control-sm ms-2 mb-2"
            style={{ width: "90%" }}
            placeholder={"\u{1F50E} Search..."}
            onChange={(e) => setDropdownFilterText(e.target.value)}
          />
        </li>

        {dropdownElementsList}
      </ul>
    </div>
  );
};

export default RadioDropdown;
